import { createSelector } from '@reduxjs/toolkit'
import { type Address, getAddress } from 'viem'
import type { EnabledVaultsInfo } from '../types'

export const TTL_INFINITY = BigInt('0x7FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF') // 2^255 - 1 represents the maximum value for a signed 256-bit integer
export const TTL_MORE_THAN_ONE_YEAR = TTL_INFINITY - BigInt(1)
export const TTL_LIQUIDATION = -BigInt(1)
export const TTL_ERROR = -BigInt(2)

export type FormatTTLReturnType =
  | (
      | {
          value: number | string
          key?: never
          type: 'success' | 'error' | 'warning' | 'info'
        }
      | {
          value?: never
          key: string
          type: 'success' | 'error' | 'warning' | 'info'
        }
    )
  | undefined

export const formatTtl = createSelector([(ttl?: bigint) => ttl], (ttl): FormatTTLReturnType => {
  if (ttl === TTL_INFINITY) return { key: 'infinite', type: 'success' }
  if (ttl === TTL_MORE_THAN_ONE_YEAR) return { key: 'moreThanOneYear', type: 'success' }
  if (ttl === TTL_LIQUIDATION) return { key: 'liquidated', type: 'error' }
  if (ttl === TTL_ERROR) return { key: 'ttlError', type: 'error' }
  if (ttl === 0n) return { key: 'lessThanADay', type: 'error' }
  if (!ttl || isNaN(Number(ttl))) return undefined

  const days = Number(ttl)

  if (Number(ttl) < 1) {
    return { key: 'lessThanADay', type: 'error' }
  } else if (days < 2) {
    return { value: days, type: 'error' }
  } else if (days < 7) {
    return { value: days, type: 'warning' }
  } else if (days < 14) {
    return { value: days, type: 'info' }
  }
  return { value: days, type: 'success' }
})

export const reduceTTL = createSelector([(accountStatus: EnabledVaultsInfo[]) => accountStatus], (accountStatus) => {
  const data = new Map<Address, bigint>()
  for (const status of accountStatus) {
    if (!status) continue
    const vaultCheck = new Set<Address>()
    const { vaultAccountInfo, evcAccountInfo } = status
    const ttl: bigint[] = []
    for (const vault of vaultAccountInfo) {
      if (
        !vaultCheck.has(getAddress(vault.vault)) &&
        vault.isController &&
        vault.liquidityInfo.liabilityValueBorrowing > 0n
      ) {
        vaultCheck.add(getAddress(vault.vault))
        ttl.push(vault.liquidityInfo.timeToLiquidation)
      }
    }

    if (ttl.length === 1) {
      // @ts-ignore - ttl[0] might be undefined
      data.set(getAddress(evcAccountInfo.account), ttl[0])
    }
  }

  return data
})
